import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  FINANCIAL_ROWS,
  FinancialFieldKey,
  FinancialReportView,
  RegisterSaveFn,
  ProfitFormValues,
} from "../../types";

export const useFinancialReportForm = (
  report: FinancialReportView,
  onRegisterSave?: (_fn: RegisterSaveFn) => void,
) => {
  const {
    control,
    getValues,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm<Record<FinancialFieldKey, string>>({
    defaultValues: {},
  });

  useEffect(() => {
    const values = FINANCIAL_ROWS.reduce(
      (acc, row) => {
        const value = report[row.key];
        acc[row.key] = value == null ? "" : String(value);
        return acc;
      },
      {} as Record<FinancialFieldKey, string>,
    );

    reset(values, {
      keepDirty: false,
    });
  }, [report, reset]);

  const watched = useWatch({ control }) as Partial<
    Record<FinancialFieldKey, string>
  >;

  const getPayload = useMemo(() => {
    return (): ProfitFormValues | null => {
      const raw = getValues();

      const payload: ProfitFormValues = {};

      const toNumber = (v: string | number | undefined): number => {
        if (typeof v === "number") return v;
        if (typeof v === "string") return Number(v);
        return 0;
      };

      for (const key of Object.keys(dirtyFields) as FinancialFieldKey[]) {
        if (!dirtyFields[key]) continue;

        const value = raw[key];

        // ❌ boş field varsa direkt INVALID
        if (value === "" || value == null) {
          return null;
        }

        payload[key] = toNumber(value);
      }

      delete payload.profit;

      // ❌ hiçbir değişiklik yoksa INVALID
      if (Object.keys(payload).length === 0) {
        return null;
      }

      return payload;
    };
  }, [getValues, dirtyFields]);

  useEffect(() => {
    if (!onRegisterSave) return;

    onRegisterSave((emit) => {
      const payload = getPayload();

      // ✅ sadece valid payload emit edilir
      if (payload) {
        emit(payload);
      }

      return {
        isDirty: isDirty && payload !== null, // 🔥 kritik fix
        reset: () => reset(),
      };
    });
  }, [onRegisterSave, getPayload, isDirty, reset]);

  return {
    control,
    watched,
    isDirty,
    getPayload,
  };
};
